import { useEffect, useMemo, useRef, useState } from 'react';
import {
  SAFE_LEGACY_ROUTE,
  fetchUiSurfaceBootstrap,
} from './uiSurfaceBootstrap';
import { UIUX_THREE_TENANT_PILOT_RELEASE_ID } from './uiuxPilotRelease';
import { markUiSurfaceEntryAttempt } from './uiSurfaceEntryAttempt';

const RELEASE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/;
const APP_ROUTE_PATTERN = /^\/app\/[A-Za-z0-9/_-]+$/;
const DEFAULT_REVALIDATION_INTERVAL_MS = 60_000;

const initialBoundaryState = () => ({
  status: 'loading',
  decision: null,
  reason: null,
});

export const createBearerAuthorization = (token) => {
  const normalized = typeof token === 'string' ? token.trim() : '';
  if (!normalized || normalized.length > 8192 || /[\r\n]/.test(normalized)) return null;
  return `Bearer ${normalized}`;
};

export const isValidUiReleaseId = (value) => (
  typeof value === 'string' && RELEASE_ID_PATTERN.test(value)
);

const isSafeNewAppRoute = (value) => (
  typeof value === 'string'
  && APP_ROUTE_PATTERN.test(value)
  && !value.includes('//')
);

export const evaluateNewSurfaceDecision = (decision, expectedReleaseId) => {
  if (!isValidUiReleaseId(expectedReleaseId)) {
    return { allowed: false, reason: 'expected-release-invalid' };
  }
  if (!decision || decision.effectiveSurface !== 'new' || decision.safeFallback === true) {
    return { allowed: false, reason: decision?.reason || 'legacy-decision' };
  }
  if (!isValidUiReleaseId(decision.releaseId)) {
    return { allowed: false, reason: 'release-invalid' };
  }
  if (decision.releaseId !== expectedReleaseId) {
    return { allowed: false, reason: 'release-mismatch' };
  }
  if (decision.rollbackAvailable !== true) {
    return { allowed: false, reason: 'rollback-unavailable' };
  }
  if (!isSafeNewAppRoute(decision.routes?.new)) {
    return { allowed: false, reason: 'new-route-invalid' };
  }
  return { allowed: true, reason: 'new-surface-approved' };
};

const normalizedLocationPath = (locationObject) => {
  const pathname = String(locationObject?.pathname || '').replace(/\/+$/, '') || '/';
  const search = String(locationObject?.search || '');
  const hash = String(locationObject?.hash || '');
  return { pathname, search, hash };
};

export const replaceWithFixedLegacyRoute = (locationObject) => {
  if (!locationObject) return 'location-unavailable';
  const current = normalizedLocationPath(locationObject);
  if (current.pathname === SAFE_LEGACY_ROUTE && !current.search && !current.hash) {
    return 'already-legacy';
  }
  if (typeof locationObject.replace !== 'function') return 'location-unavailable';
  try {
    locationObject.replace(SAFE_LEGACY_ROUTE);
    return 'redirected';
  } catch (_error) {
    return 'redirect-failed';
  }
};

function BoundaryMessage({ status, redirectOutcome }) {
  if (status === 'loading') {
    return (
      <div role="status" aria-live="polite" aria-busy="true">
        <p>利用する画面を確認しています。</p>
      </div>
    );
  }

  const redirectFailed = redirectOutcome === 'location-unavailable'
    || redirectOutcome === 'redirect-failed';
  return (
    <div role="alert" aria-live="assertive" aria-busy="false">
      <p>
        {redirectFailed
          ? '新しい画面を安全に確認できませんでした。旧画面を開いてください。'
          : redirectOutcome === 'already-legacy'
            ? '旧画面を表示しています。'
            : '新しい画面を安全に確認できないため、旧画面へ移動しています。'}
      </p>
      <a href={SAFE_LEGACY_ROUTE}>旧画面を開く</a>
    </div>
  );
}

export function UiSurfaceBoundary({
  authenticatedUser,
  token,
  expectedReleaseId = UIUX_THREE_TENANT_PILOT_RELEASE_ID,
  children = null,
  newSurface,
  fetchImpl,
  timeoutMs = 5000,
  revalidationIntervalMs = DEFAULT_REVALIDATION_INTERVAL_MS,
  locationObject = typeof window === 'undefined' ? null : window.location,
  eventTarget = typeof window === 'undefined' ? null : window,
  documentObject = typeof document === 'undefined' ? null : document,
  sessionStorageObject = typeof window === 'undefined' ? null : window.sessionStorage,
}) {
  const userId = authenticatedUser?.id;
  const userRole = authenticatedUser?.role;
  const serverDecisionUser = useMemo(() => ({ id: userId, role: userRole }), [userId, userRole]);
  const [boundaryState, setBoundaryState] = useState(initialBoundaryState);
  const [redirectOutcome, setRedirectOutcome] = useState('idle');
  const redirectAttempted = useRef(false);

  useEffect(() => {
    let active = true;
    let terminal = false;
    let requestInFlight = false;
    redirectAttempted.current = false;
    setRedirectOutcome('idle');
    setBoundaryState(initialBoundaryState());

    const authHeader = createBearerAuthorization(token);
    const revalidate = async () => {
      if (!active || terminal || requestInFlight) return;
      requestInFlight = true;
      try {
        const decision = await fetchUiSurfaceBootstrap({
          authenticatedUser: serverDecisionUser,
          authHeader,
          fetchImpl,
          timeoutMs,
        });
        if (!active) return;
        const evaluation = evaluateNewSurfaceDecision(decision, expectedReleaseId);
        if (!evaluation.allowed) terminal = true;
        setBoundaryState(evaluation.allowed
          ? { status: 'ready', decision, reason: evaluation.reason }
          : { status: 'legacy', decision, reason: evaluation.reason });
      } catch (_error) {
        if (!active) return;
        terminal = true;
        setBoundaryState({ status: 'legacy', decision: null, reason: 'bootstrap-unexpected-error' });
      } finally {
        requestInFlight = false;
      }
    };
    const intervalMs = Number.isFinite(Number(revalidationIntervalMs)) && Number(revalidationIntervalMs) > 0
      ? Number(revalidationIntervalMs)
      : DEFAULT_REVALIDATION_INTERVAL_MS;
    const handleFocus = () => { revalidate(); };
    const handleVisibility = () => {
      if (!documentObject || documentObject.visibilityState === 'visible') revalidate();
    };

    revalidate();
    const intervalId = setInterval(revalidate, intervalMs);
    eventTarget?.addEventListener?.('focus', handleFocus);
    documentObject?.addEventListener?.('visibilitychange', handleVisibility);

    return () => {
      active = false;
      clearInterval(intervalId);
      eventTarget?.removeEventListener?.('focus', handleFocus);
      documentObject?.removeEventListener?.('visibilitychange', handleVisibility);
    };
  }, [documentObject, eventTarget, expectedReleaseId, fetchImpl, revalidationIntervalMs, serverDecisionUser, timeoutMs, token]);

  useEffect(() => {
    if (boundaryState.status !== 'legacy' || redirectAttempted.current) return;
    redirectAttempted.current = true;
    markUiSurfaceEntryAttempt(sessionStorageObject, expectedReleaseId, userId);
    setRedirectOutcome(replaceWithFixedLegacyRoute(locationObject));
  }, [boundaryState.status, expectedReleaseId, locationObject, sessionStorageObject, userId]);

  if (boundaryState.status !== 'ready') {
    return <BoundaryMessage status={boundaryState.status} redirectOutcome={redirectOutcome} />;
  }

  const approvedSurface = newSurface === undefined ? children : newSurface;
  return typeof approvedSurface === 'function'
    ? approvedSurface(boundaryState.decision)
    : approvedSurface;
}

export default UiSurfaceBoundary;

export { DEFAULT_REVALIDATION_INTERVAL_MS };
